import { apiSlice } from 'store/api/apiSlice';
import { peelYamiData } from 'utils/yamiEnvelope';
import { LoginType } from './types';

type YamiLoginUser = {
  id?: string;
  username?: string | null;
  firstName?: string;
  lastName?: string;
  balance?: number;
  role?: string;
  retailShopId?: string | null;
  cashierTillBalance?: number;
  cashierPermissions?: CashierPermissions;
};

type CashierPermissions = {
  canCancel: boolean;
  cancelWindowMinutes: number;
  canReprint: boolean;
  canRedeem: boolean;
  maxSingleRedeemAmount: number;
};

type YamiLoginShop = {
  id?: string;
  code?: string;
  name?: string;
  agentName?: string;
  min_stake?: string;
  max_stake?: string;
  is_active?: boolean;
  balance?: string;
  /** Retail shop drawer float (main-service login payload) */
  till_balance?: number;
  tillBalance?: number;
};

function resolveCashierTillBalance(
  shop: YamiLoginShop | null | undefined,
  userTill: unknown
): number {
  const fromUser = typeof userTill === 'number' && Number.isFinite(userTill) ? userTill : 0;
  if (!shop) return fromUser;
  if (typeof shop.till_balance === 'number' && Number.isFinite(shop.till_balance)) {
    return shop.till_balance;
  }
  if (typeof shop.tillBalance === 'number' && Number.isFinite(shop.tillBalance)) {
    return shop.tillBalance;
  }
  const parsed = parseFloat(String(shop.balance ?? ''));
  if (Number.isFinite(parsed)) return parsed;
  return fromUser;
}

type YamiLoginRaw = {
  success?: boolean;
  ok?: boolean;
  message?: string;
  token?: string;
  user?: YamiLoginUser;
  shop?: YamiLoginShop | null;
  data?: {
    token?: string;
    user?: YamiLoginUser;
    shop?: YamiLoginShop | null;
  };
};

type YamiProfileRaw = {
  success?: boolean;
  ok?: boolean;
  user?: {
    id?: string;
    balance?: number;
    role?: string;
    retailShopId?: string | null;
    cashierTillBalance?: number;
    cashierPermissions?: CashierPermissions;
  };
  data?: {
    user?: YamiProfileRaw['user'];
  };
};

function unwrapLoginPayload(res: YamiLoginRaw): {
  token: string | undefined;
  user: YamiLoginUser | undefined;
  shop: YamiLoginShop | null | undefined;
} {
  const inner = res.data && typeof res.data === 'object' ? res.data : res;
  return {
    token: inner.token ?? res.token,
    user: inner.user ?? res.user,
    shop: inner.shop !== undefined ? inner.shop : res.shop,
  };
}

function unwrapProfileUser(res: YamiProfileRaw) {
  if (res.data && typeof res.data === 'object' && res.data.user) return res.data.user;
  return res.user;
}

function shopIdToNumber(id: unknown): number {
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  if (typeof id === 'string' && id.length > 0) {
    const n = parseInt(id.slice(-8), 16);
    if (Number.isFinite(n)) return n;
  }
  return 1;
}

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginType, { username: string; password: string }>({
      query: (body) => ({
        url: '/api/auth/login',
        method: 'POST',
        body: {
          username: body.username.trim(),
          password: body.password,
          cashierApp: true,
        },
      }),
      transformResponse: (res: YamiLoginRaw): LoginType => {
        const { token, user: u, shop } = unwrapLoginPayload(res);
        const ok = res.ok === true || res.success === true;
        if (!ok || !token || !u) {
          throw new Error('Invalid credentials');
        }
        const roleLc = String(u.role || '').toLowerCase();
        if (roleLc !== 'cashier' || shop == null) {
          throw new Error('Invalid credentials');
        }
        const sid = shopIdToNumber(shop.id);
        const displayLogin = (typeof u.username === 'string' && u.username.trim()) || '';
        return {
          user: {
            id: sid,
            username: displayLogin,
            role: 'cashier',
            cashierPermissions: u.cashierPermissions,
            shop: sid,
            password_changed: false,
          },
          tokens: { access: token, refresh: token },
          shop: {
            id: sid,
            balance: shop.balance ?? String(u.balance ?? 0),
            code: shop.code ? String(shop.code) : '',
            name: shop.name ? String(shop.name) : 'Shop',
            agentName: shop.agentName ? String(shop.agentName) : '',
            min_stake: shop.min_stake ?? '1',
            max_stake: shop.max_stake ?? '100000',
            is_active: shop.is_active !== false,
            cashierTillBalance: resolveCashierTillBalance(shop, u.cashierTillBalance),
          },
          tenant: 'public',
        };
      },
    }),
    /** Sync float + role after admin deposits (or token rehydrate). */
    getCashierProfile: builder.query<
      { balance: number; tillBalance: number; role: string; cashierPermissions?: CashierPermissions },
      void
    >({
      query: () => ({ url: '/api/auth/profile', method: 'GET' }),
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      providesTags: ['CashierProfile'],
      transformResponse: (res: unknown) => {
        const root = (res && typeof res === 'object' ? res : {}) as YamiProfileRaw;
        const peeled = peelYamiData(res) as Record<string, unknown>;
        const u =
          unwrapProfileUser(root) ||
          (peeled.user as YamiProfileRaw['user']) ||
          (peeled.data as { user?: YamiProfileRaw['user'] } | undefined)?.user;
        const ok =
          root.ok === true ||
          root.success === true ||
          Boolean(u && typeof u === 'object');
        if (!ok || !u) {
          throw new Error('Profile unavailable');
        }
        const till =
          typeof u.cashierTillBalance === 'number' && Number.isFinite(u.cashierTillBalance)
            ? u.cashierTillBalance
            : 0;
        return {
          balance: typeof u.balance === 'number' ? u.balance : 0,
          tillBalance: till,
          role: u.role ?? 'user',
          cashierPermissions: u.cashierPermissions,
        };
      },
    }),
    changePassword: builder.mutation<{ password_changed: boolean }, { current_password: string; new_password: string }>({
      query: (body) => ({
        url: '/api/auth/change-password',
        method: 'POST',
        body: {
          currentPassword: body.current_password,
          newPassword: body.new_password,
        },
      }),
      transformResponse: () => ({ password_changed: true }),
    }),
  }),
});

export const { useLoginMutation, useLazyGetCashierProfileQuery, useChangePasswordMutation } =
  authApiSlice;
